import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, KeyRound, Loader2, LogOut, Mail, Phone, RefreshCw, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "opeam_admin_key";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(() => Boolean(sessionStorage.getItem(KEY_STORAGE)));
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const k = sessionStorage.getItem(KEY_STORAGE);
    if (!k) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
      setEnquiries(data);
      setAuthError("");
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(KEY_STORAGE);
        setAuthed(false);
        setAuthError("That key didn't work. Please try again.");
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const unlock = (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    sessionStorage.setItem(KEY_STORAGE, key.trim());
    setAuthed(true);
  };

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAuthed(false);
    setKey("");
    setEnquiries([]);
  };

  const remove = async (id) => {
    if (deleting) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(KEY_STORAGE) },
      });
      setEnquiries((prev) => prev.filter((x) => x.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="admin-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/opeam-logo-a.png" alt="OPEAM Group" className="h-8 w-auto" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-copper">Enquiries</span>
          </div>
          <div className="flex items-center gap-3">
            {authed && (
              <>
                <button
                  data-testid="admin-refresh-button"
                  onClick={load}
                  className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
                  aria-label="Refresh"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
                  aria-label="Log out"
                >
                  <LogOut size={14} />
                </button>
              </>
            )}
            <a
              data-testid="admin-back-link"
              href="/"
              className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
              aria-label="Back to site"
            >
              <ArrowLeft size={14} />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
        {!authed ? (
          <motion.form
            data-testid="admin-login-form"
            onSubmit={unlock}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md mx-auto mt-10"
          >
            <KeyRound size={26} strokeWidth={1.25} className="text-copper mb-8" />
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
              Private <span className="italic text-copper">enquiries.</span>
            </h1>
            <p className="mt-4 text-sm text-bone/50 leading-relaxed">
              Enter your admin key to view messages from the website contact form.
            </p>
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="mt-8 w-full bg-transparent border border-white/20 px-5 py-4 text-sm text-bone placeholder:text-bone/30 focus:outline-none focus:border-copper focus:ring-1 focus:ring-copper transition-colors duration-300"
            />
            {authError && (
              <p data-testid="admin-auth-error" className="mt-3 text-xs text-red-400">{authError}</p>
            )}
            <button
              data-testid="admin-unlock-button"
              type="submit"
              className="btn-swipe mt-6 w-full border border-copper text-copper px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-300"
            >
              View enquiries
            </button>
          </motion.form>
        ) : (
          <>
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs tracking-[0.35em] uppercase text-copper">Inbox</p>
                <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">
                  Website <span className="italic text-copper">enquiries.</span>
                </h1>
              </div>
              <span data-testid="admin-count" className="font-display italic text-2xl text-bone/40">
                {enquiries.length} {enquiries.length === 1 ? "message" : "messages"}
              </span>
            </div>

            {loading && enquiries.length === 0 ? (
              <div className="flex justify-center py-24" data-testid="admin-loading">
                <Loader2 size={24} className="animate-spin text-copper" />
              </div>
            ) : enquiries.length === 0 ? (
              <div className="border border-white/10 py-24 flex flex-col items-center gap-4 text-bone/40" data-testid="admin-empty">
                <Inbox size={28} strokeWidth={1.25} />
                <p className="text-sm tracking-wide">No enquiries yet.</p>
              </div>
            ) : (
              <div className="space-y-px bg-white/10 border border-white/10" data-testid="admin-enquiry-list">
                <AnimatePresence>
                  {enquiries.map((en) => (
                    <motion.article
                      key={en.id}
                      data-testid={`admin-enquiry-${en.id}`}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.35 }}
                      className="bg-ink p-6 sm:p-8 group"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="font-display text-2xl tracking-tight">{en.name}</h2>
                            <span className="text-[10px] tracking-[0.2em] uppercase text-copper border border-copper/40 px-2.5 py-1">
                              {en.interest}
                            </span>
                            {en.lang === "es" && (
                              <span className="text-[10px] tracking-[0.2em] uppercase text-bone/50 border border-white/20 px-2.5 py-1">
                                ES
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-[11px] tracking-[0.15em] uppercase text-bone/30">
                            {formatDate(en.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            data-testid={`admin-enquiry-email-${en.id}`}
                            href={`mailto:${en.email}`}
                            className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
                            aria-label={`Email ${en.name}`}
                          >
                            <Mail size={14} />
                          </a>
                          {en.phone && (
                            <a
                              data-testid={`admin-enquiry-phone-${en.id}`}
                              href={`tel:${en.phone}`}
                              className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
                              aria-label={`Call ${en.name}`}
                            >
                              <Phone size={14} />
                            </a>
                          )}
                          <button
                            data-testid={`admin-enquiry-delete-${en.id}`}
                            onClick={() => remove(en.id)}
                            disabled={deleting === en.id}
                            className="w-9 h-9 border border-white/20 flex items-center justify-center text-bone/60 hover:text-red-400 hover:border-red-400/60 transition-colors duration-300 disabled:opacity-40"
                            aria-label={`Delete enquiry from ${en.name}`}
                          >
                            {deleting === en.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-sm text-bone/50">
                        <span>{en.email}</span>
                        {en.phone && <span>{en.phone}</span>}
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-bone/70 max-w-3xl whitespace-pre-wrap">
                        {en.message}
                      </p>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
